import { basicSort, COMMON_TABLE_COLUMN_WIDTHS, geneLinkSort, numericSort, TableCellInteger, TableCellLink, TableCellNumeric, TableCellPercent, TableCellPValue, variantLinkSort, } from 'Shared/components/Table';
import { ELevelResult, ETraitsTypes } from 'Shared/types';
import createTableVariantLink from 'Common/components/Table/factories/createTableVariantLink/createTableVariantLink';
import createTableGeneLink from 'Common/components/Table/factories/createTableGeneLink/createTableGeneLink';
import styles from 'Shared/components/Table/components/TableView/TableView.css';
export var ETableColumnAccessor;
(function (ETableColumnAccessor) {
    ETableColumnAccessor["Variant"] = "variant";
    ETableColumnAccessor["VariantName"] = "variant.name";
    ETableColumnAccessor["VariantType"] = "variant.type";
    ETableColumnAccessor["ConsequenceType"] = "consequenceType";
    ETableColumnAccessor["GeneName"] = "gene.name";
    ETableColumnAccessor["Ancestry"] = "ancestry";
    ETableColumnAccessor["CollapsingModelName"] = "collapsingModel.name";
    ETableColumnAccessor["TranscriptId"] = "transcriptId";
    ETableColumnAccessor["CDNAChange"] = "CDNAChange";
    ETableColumnAccessor["AAChange"] = "AAChange";
    ETableColumnAccessor["ExonRank"] = "exonRank";
    ETableColumnAccessor["Samples"] = "noSamples";
    ETableColumnAccessor["NoAAGenotypes"] = "noAAGenotypes";
    ETableColumnAccessor["NoABGenotypes"] = "noABGenotypes";
    ETableColumnAccessor["NoBBGenotypes"] = "noBBGenotypes";
    ETableColumnAccessor["MAF"] = "MAF";
    ETableColumnAccessor["AAF"] = "AAF";
    ETableColumnAccessor["ABOrBBGenotypes"] = "ABOrBBGenotypes";
    ETableColumnAccessor["BBGenotypes"] = "BBGenotypes";
    ETableColumnAccessor["PValue"] = "pvalue";
    ETableColumnAccessor["EffectSize"] = "effectSize";
    ETableColumnAccessor["EffectSizeStandardError"] = "effectSizeStandardError";
    ETableColumnAccessor["EffectSizeLCI"] = "effectSizeLCI";
    ETableColumnAccessor["EffectSizeUCI"] = "effectSizeUCI";
})(ETableColumnAccessor || (ETableColumnAccessor = {}));
/**
 * Get table columns
 *
 * @param descriptions - Text descriptions for the columns
 * @param referrerData - Referrer data for the variant link
 * @param columnsData - Columns data for the variant link
 * @param hasAncestryData - Boolean value which determines if ancestry data exists
 * @returns formatted table columns
 */
export const getTableColumns = (descriptions, referrerData, columnsData, hasAncestryData) => [
    {
        Header: 'Variant',
        accessor: ETableColumnAccessor.Variant,
        description: descriptions[ETableColumnAccessor.Variant],
        className: styles.alignLeft,
        Cell: TableCellLink({
            additionalData: columnsData,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableVariantLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT,
        exported: {
            Header: 'Variant',
            accessor: ETableColumnAccessor.VariantName,
        },
        sortType: variantLinkSort,
    },
    {
        Header: 'Variant type',
        accessor: ETableColumnAccessor.VariantType,
        description: descriptions[ETableColumnAccessor.VariantType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT_TYPE,
        sortType: basicSort,
    },
    ...(hasAncestryData ? [
        {
            Header: 'Ancestry',
            accessor: ETableColumnAccessor.Ancestry,
            description: descriptions[ETableColumnAccessor.Ancestry],
            width: COMMON_TABLE_COLUMN_WIDTHS.ANCESTRY,
            sortType: basicSort,
        },
    ] : []),
    {
        Header: 'p-value',
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: 'Model',
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: 'Consequence type',
        accessor: ETableColumnAccessor.ConsequenceType,
        description: descriptions[ETableColumnAccessor.ConsequenceType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CONSEQUENCE_TYPE,
        sortType: basicSort,
    },
    {
        Header: 'Gene',
        accessor: ETableColumnAccessor.GeneName,
        description: descriptions[ETableColumnAccessor.GeneName],
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Variant,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableGeneLink),
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    {
        Header: 'Transcript',
        accessor: ETableColumnAccessor.TranscriptId,
        description: descriptions[ETableColumnAccessor.TranscriptId],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.TRANSCRIPT,
        sortType: basicSort,
    },
    {
        Header: 'cDNA change',
        accessor: ETableColumnAccessor.CDNAChange,
        description: descriptions[ETableColumnAccessor.CDNAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CDNA_CHANGE,
        sortType: basicSort,
    },
    {
        Header: 'Amino acid change',
        accessor: ETableColumnAccessor.AAChange,
        description: descriptions[ETableColumnAccessor.AAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.AMINO_ACID_CHANGE,
        sortType: basicSort,
    },
    {
        Header: 'Exon rank',
        accessor: ETableColumnAccessor.ExonRank,
        description: descriptions[ETableColumnAccessor.ExonRank],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.EXON_RANK,
        sortType: basicSort,
    },
    {
        Header: 'No. samples',
        accessor: ETableColumnAccessor.Samples,
        description: descriptions[ETableColumnAccessor.Samples],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_SAMPLES,
    },
    {
        Header: 'No. AA genotypes',
        accessor: ETableColumnAccessor.NoAAGenotypes,
        description: descriptions[ETableColumnAccessor.NoAAGenotypes],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AA_GENOTYPES,
    },
    {
        Header: 'No. AB genotypes',
        accessor: ETableColumnAccessor.NoABGenotypes,
        description: descriptions[ETableColumnAccessor.NoABGenotypes],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AB_GENOTYPES,
    },
    {
        Header: 'No. BB genotypes',
        accessor: ETableColumnAccessor.NoBBGenotypes,
        description: descriptions[ETableColumnAccessor.NoBBGenotypes],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_BB_GENOTYPES,
    },
    {
        Header: 'AAF',
        accessor: ETableColumnAccessor.AAF,
        description: descriptions[ETableColumnAccessor.AAF],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.AAF,
    },
    {
        Header: '% AB or BB genotypes',
        accessor: ETableColumnAccessor.ABOrBBGenotypes,
        description: descriptions[ETableColumnAccessor.ABOrBBGenotypes],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion AB or BB genotypes',
            accessor: ETableColumnAccessor.ABOrBBGenotypes,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_AB_OR_BB_GENOTYPES,
    },
    {
        Header: '% BB genotypes',
        accessor: ETableColumnAccessor.BBGenotypes,
        description: descriptions[ETableColumnAccessor.BBGenotypes],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion BB genotypes',
            accessor: ETableColumnAccessor.BBGenotypes,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_BB_GENOTYPES,
    },
    {
        Header: 'Effect size',
        accessor: ETableColumnAccessor.EffectSize,
        description: descriptions[ETableColumnAccessor.EffectSize],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE,
    },
    {
        Header: 'Effect size standard error',
        accessor: ETableColumnAccessor.EffectSizeStandardError,
        description: descriptions[ETableColumnAccessor.EffectSizeStandardError],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_STANDARD_ERROR,
    },
    {
        Header: 'Effect size LCI',
        accessor: ETableColumnAccessor.EffectSizeLCI,
        description: descriptions[ETableColumnAccessor.EffectSizeLCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_LCI,
    },
    {
        Header: 'Effect size UCI',
        accessor: ETableColumnAccessor.EffectSizeUCI,
        description: descriptions[ETableColumnAccessor.EffectSizeUCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_UCI,
    },
];
export const getFinngenTableColumns = (descriptions, referrerData, columnsData) => [
    {
        Header: 'Variant',
        accessor: ETableColumnAccessor.Variant,
        description: descriptions[ETableColumnAccessor.Variant],
        className: styles.alignLeft,
        Cell: TableCellLink({
            additionalData: columnsData,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableVariantLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT,
        exported: {
            Header: 'Variant',
            accessor: ETableColumnAccessor.VariantName,
        },
        sortType: variantLinkSort,
    },
    {
        Header: 'Variant type',
        accessor: ETableColumnAccessor.VariantType,
        description: descriptions[ETableColumnAccessor.VariantType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT_TYPE,
        sortType: basicSort,
    },
    {
        Header: 'p-value',
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: 'Model',
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: 'Consequence type',
        accessor: ETableColumnAccessor.ConsequenceType,
        description: descriptions[ETableColumnAccessor.ConsequenceType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CONSEQUENCE_TYPE,
        sortType: basicSort,
    },
    {
        Header: 'Gene',
        accessor: ETableColumnAccessor.GeneName,
        description: descriptions[ETableColumnAccessor.GeneName],
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Variant,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableGeneLink),
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    {
        Header: 'Transcript',
        accessor: ETableColumnAccessor.TranscriptId,
        description: descriptions[ETableColumnAccessor.TranscriptId],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.TRANSCRIPT,
        sortType: basicSort,
    },
    {
        Header: 'cDNA change',
        accessor: ETableColumnAccessor.CDNAChange,
        description: descriptions[ETableColumnAccessor.CDNAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CDNA_CHANGE,
        sortType: basicSort,
    },
    {
        Header: 'Amino acid change',
        accessor: ETableColumnAccessor.AAChange,
        description: descriptions[ETableColumnAccessor.AAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.AMINO_ACID_CHANGE,
        sortType: basicSort,
    },
    {
        Header: 'Exon rank',
        accessor: ETableColumnAccessor.ExonRank,
        description: descriptions[ETableColumnAccessor.ExonRank],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.EXON_RANK,
        sortType: basicSort,
    },
    {
        Header: 'AAF',
        accessor: ETableColumnAccessor.AAF,
        description: descriptions[ETableColumnAccessor.AAF],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.AAF,
    },
    {
        Header: 'Effect size',
        accessor: ETableColumnAccessor.EffectSize,
        description: descriptions[ETableColumnAccessor.EffectSize],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE,
    },
    {
        Header: 'Effect size standard error',
        accessor: ETableColumnAccessor.EffectSizeStandardError,
        description: descriptions[ETableColumnAccessor.EffectSizeStandardError],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_STANDARD_ERROR,
    },
    {
        Header: 'Effect size LCI',
        accessor: ETableColumnAccessor.EffectSizeLCI,
        description: descriptions[ETableColumnAccessor.EffectSizeLCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_LCI,
    },
    {
        Header: 'Effect size UCI',
        accessor: ETableColumnAccessor.EffectSizeUCI,
        description: descriptions[ETableColumnAccessor.EffectSizeUCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_UCI,
    },
];
